import React, { useState } from 'react';
import {
  Typography,
  Paper,
  Box,
  Modal,
  IconButton,
  Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import CustomSelect from '../../../components/CustomSelect/CustomSelect';

interface FilterConfig {
  labelId: string;
  name: string;
  value: string;
  onChange: (value: string) => void;
  options: { label: string; value: string }[];
  placeholder: string;
  placeholderIcon: React.ReactNode;
  visible?: boolean;
}

interface ProjectFiltersModalProps {
  filters: FilterConfig[];
  activeFiltersCount: number;
  onClearFilters: () => void;
}

const ProjectFiltersModal: React.FC<ProjectFiltersModalProps> = ({
  filters,
  activeFiltersCount,
  onClearFilters,
}) => {
  const [open, setOpen] = useState(false);

  const visibleFilters = filters.filter((f) => f.visible !== false);

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        sx={{
          borderRadius: '12px',
          border: '1px solid var(--border)',
          bgcolor: 'var(--surface)',
          color: 'var(--text-2)',
          display: 'flex',
          alignItems: 'center',
          textTransform: 'none',
          px: '16px',
          minWidth: 'fit-content',
          '& .MuiButton-startIcon': {
            marginRight: '8px',
          },
        }}
        startIcon={<FilterListIcon />}
      >
        Filters
        {activeFiltersCount > 0 && (
          <span className="ml-2 text-[14px] font-[450] text-text-2">
            ({activeFiltersCount} Selected)
          </span>
        )}
      </Button>

      <Modal open={open} onClose={() => setOpen(false)}>
        <Box
          component={Paper}
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: 'calc(100vw - 32px)', sm: '700px' },
            maxWidth: '700px',
            maxHeight: '90vh',
            bgcolor: 'var(--surface)',
            borderRadius: '12px',
            boxShadow: 24,
            display: 'flex',
            flexDirection: 'column',
            padding: '2px',
          }}
        >
          {/* Header */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ padding: '16px' }}
          >
            <Typography
              sx={{
                fontFamily: '"Circular Std", sans-serif',
                fontSize: '24px',
                fontWeight: 900,
              }}
            >
              Filters
            </Typography>
            <IconButton onClick={() => setOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
          <hr style={{ margin: '0px 16px', color: 'var(--border)' }} />

          {/* Filters Grid */}
          <Box
            sx={{
              flexGrow: 1,
              maxHeight: '500px',
              overflowY: 'auto',
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
              gap: '16px',
              padding: '24px 16px',
            }}
          >
            {visibleFilters.map((filter) => (
              <Box key={filter.labelId} sx={{ overflow: 'hidden', minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 500,
                    color: 'var(--text-2)',
                    mb: '8px',
                  }}
                >
                  {filter.placeholder}
                </Typography>
                <CustomSelect
                  labelId={filter.labelId}
                  name={filter.name}
                  value={filter.value}
                  onChange={filter.onChange}
                  options={filter.options}
                  placeholder={filter.placeholder}
                  showPlaceholderIcon
                  placeholderIcon={filter.placeholderIcon}
                  width={'100%' as any}
                />
              </Box>
            ))}
          </Box>

          {/* Footer */}
          <Box display="flex" justifyContent="flex-end" my="16px">
            <Button
              variant="outlined"
              onClick={() => {
                onClearFilters();
                setOpen(false);
              }}
              sx={{
                mr: 2,
                textTransform: 'capitalize',
                color: 'var(--accent-text)',
                borderColor: 'var(--accent-text)',
                '&:hover': { bgcolor: 'var(--tint)' },
              }}
            >
              Clear Filters
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default ProjectFiltersModal;
